import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import i18n, { DEFAULT_LANGUAGE } from '@/lib/i18n';
import { formatMoney } from '@/lib/format-money';

/**
 * Combines class names using clsx and tailwind-merge
 * Useful for conditional classes and avoiding Tailwind conflicts
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Debounce function for performance optimization
 */
export function debounce<T extends (...args: Parameters<T>) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, wait);
  };
}

/**
 * Generate a unique ID
 */
export function generateId(): string {
  return crypto.randomUUID();
}

/**
 * Format price with currency in the shop language. The language defaults to
 * the active i18next language (the default language before i18next boots).
 */
export function formatPrice(
  price: number,
  currency = 'EUR',
  language: string = i18n.language || DEFAULT_LANGUAGE
): string {
  return formatMoney(price, currency, language);
}
